import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { NoteHistoryDialogProps } from './types';
import { getActionColor, formatDate, formatTime } from './utils';

const NoteHistoryDialog = ({ open, onClose, history, loading }: NoteHistoryDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Note History</DialogTitle>
      <DialogContent sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No history available.
          </Typography>
        ) : (
          <Box sx={{ position: 'relative', ml: 2.5, mt: 1 }}>
            {/* Timeline line */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 16,
                bottom: 16,
                width: 2,
                bgcolor: 'divider',
                borderRadius: 1,
              }}
            />

            {history.map((entry, index) => (
              <Box
                key={entry.id}
                sx={{
                  position: 'relative',
                  pl: 3,
                  pb: index < history.length - 1 ? 2.5 : 0,
                }}
              >
                {/* Timeline dot — vertically centered to the header row */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: -5,
                    top: 16,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: `${getActionColor(entry.action)}.main`,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    zIndex: 1,
                  }}
                />

                {/* Entry card */}
                <Box
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 1.5,
                    p: 1.5,
                  }}
                >
                  {/* Header: action chip + author + date */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={entry.action}
                        size="small"
                        color={getActionColor(entry.action)}
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        by {entry.performed_by_name || 'System'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(entry.performed_at)}, {formatTime(entry.performed_at)}
                    </Typography>
                  </Box>

                  {/* Field changes */}
                  {entry.field_changes && Object.keys(entry.field_changes).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {Object.entries(entry.field_changes).map(([field, values]) => (
                        <Box key={field} sx={{ mb: 0.75 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: 'capitalize', display: 'block', mb: 0.25 }}
                          >
                            {field.replace(/_/g, ' ')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: '#fce4ec',
                                color: '#c62828',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                textDecoration: 'line-through',
                                wordBreak: 'break-word',
                              }}
                            >
                              {String(values.old ?? 'empty')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '20px' }}>
                              →
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                wordBreak: 'break-word',
                              }}
                            >
                              {String(values.new ?? 'empty')}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteHistoryDialog;
